import type { MeResponse } from "@safeplate/contracts";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@safeplate/ui";
import {
  ArrowLeft,
  Building2,
  Check,
  Mail,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";

import { createOrganisation } from "../api/client.js";
import { displayError } from "../api/errors.js";
import { Brand } from "../components/brand.js";
import { invitationTokenFromInput } from "../auth/invitations.js";

function OrganisationForm({
  me,
  onCreated,
  onBack,
}: {
  me: MeResponse;
  onCreated: () => Promise<void>;
  onBack: () => void;
}) {
  const [legalName, setLegalName] = useState("");
  const [tradingName, setTradingName] = useState("");
  const [billingEmail, setBillingEmail] = useState(me.user.email);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idempotencyKey = useRef(globalThis.crypto.randomUUID());

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await createOrganisation(
        {
          billing_email: billingEmail.trim() || null,
          legal_name: legalName.trim(),
          trading_name: tradingName.trim() || null,
        },
        idempotencyKey.current,
      );
      await onCreated();
    } catch (caught) {
      setError(displayError(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="min-h-dvh bg-canvas px-5 py-8 sm:px-8 sm:py-12">
      <div className="mx-auto max-w-5xl">
        <Brand />
        <div className="mt-10 grid items-start gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-14">
          <div className="pt-3">
            <p className="text-xs font-bold tracking-[0.14em] text-emerald-800 uppercase">
              First step
            </p>
            <h1 className="mt-4 text-4xl leading-tight font-black tracking-[-0.05em] text-stone-950 sm:text-5xl">
              Tell us who keeps these records.
            </h1>
            <p className="mt-5 max-w-md text-base leading-7 text-stone-600">
              An organisation holds your people, locations and evidence. You’ll
              be its first administrator and can invite managers after setup.
            </p>
            <ul className="mt-8 space-y-4 text-sm text-stone-700">
              {[
                "Your legal operator name appears on verifier reports",
                "Each restaurant or kitchen is added as a licensed location",
                "You can update these details later",
              ].map((item) => (
                <li className="flex gap-3" key={item}>
                  <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-800">
                    <Check className="size-3" aria-hidden="true" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <Card className="overflow-hidden shadow-lg">
            <CardHeader className="border-b border-stone-100 p-6 sm:p-7">
              <span className="mb-3 grid size-10 place-items-center rounded-xl bg-emerald-100 text-emerald-900">
                <Building2 className="size-5" aria-hidden="true" />
              </span>
              <CardTitle className="text-xl">Organisation details</CardTitle>
              <CardDescription>
                Use the registered food-business operator’s details.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 sm:p-7">
              <Button
                className="mb-5 -ml-2"
                onClick={onBack}
                size="sm"
                type="button"
                variant="ghost"
              >
                <ArrowLeft className="size-4" aria-hidden="true" />
                Back
              </Button>
              <form
                className="space-y-5"
                onSubmit={(event) => void submit(event)}
              >
                <div className="space-y-2">
                  <Label htmlFor="legal-name">Legal operator name</Label>
                  <Input
                    autoComplete="organization"
                    id="legal-name"
                    onChange={(event) => setLegalName(event.target.value)}
                    placeholder="Kōwhai Hospitality Limited"
                    required
                    value={legalName}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trading-name">Trading name</Label>
                  <Input
                    id="trading-name"
                    onChange={(event) => setTradingName(event.target.value)}
                    placeholder="Kōwhai Kitchen"
                    value={tradingName}
                  />
                  <p className="text-xs leading-5 text-stone-500">
                    Optional, if customers know you by another name.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing-email">Billing email</Label>
                  <Input
                    autoComplete="email"
                    id="billing-email"
                    onChange={(event) => setBillingEmail(event.target.value)}
                    required
                    type="email"
                    value={billingEmail}
                  />
                </div>
                {error ? (
                  <p
                    className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-800"
                    role="alert"
                  >
                    {error}
                  </p>
                ) : null}
                <Button className="w-full" disabled={pending} type="submit">
                  {pending ? "Creating organisation…" : "Create organisation"}
                </Button>
                <p className="flex items-start gap-2 text-xs leading-5 text-stone-500">
                  <ShieldCheck
                    className="mt-0.5 size-4 shrink-0 text-emerald-700"
                    aria-hidden="true"
                  />
                  Your account becomes the first administrator. SafePlate keeps
                  each organisation’s data isolated.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
}

export function OrganisationOnboarding({
  me,
  onCreated,
}: {
  me: MeResponse;
  onCreated: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState<"choose" | "create" | "join">("choose");
  const [invitationInput, setInvitationInput] = useState("");
  const [invitationError, setInvitationError] = useState<string | null>(null);

  if (step === "create") {
    return (
      <OrganisationForm
        me={me}
        onBack={() => setStep("choose")}
        onCreated={onCreated}
      />
    );
  }

  function join(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const token = invitationTokenFromInput(invitationInput);
    if (!token) {
      setInvitationError(
        "Paste the complete invitation link sent by your manager.",
      );
      return;
    }
    void navigate({ search: { token }, to: "/invitations/accept" });
  }

  return (
    <main className="min-h-dvh bg-canvas px-5 py-8 sm:px-8 sm:py-12">
      <div className="mx-auto max-w-3xl">
        <Brand />
        <div className="mt-10">
          <p className="text-xs font-bold tracking-[0.14em] text-emerald-800 uppercase">
            Welcome to SafePlate
          </p>
          <h1 className="mt-4 text-4xl leading-tight font-black tracking-[-0.05em] text-stone-950 sm:text-5xl">
            How will you use SafePlate?
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-stone-600">
            Create a food business if you will manage it, or accept a secure
            invitation to join an existing workplace.
          </p>
        </div>

        {step === "choose" ? (
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <button
              className="rounded-2xl border border-stone-200 bg-surface p-6 text-left shadow-sm transition hover:border-emerald-400 hover:shadow-md focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:outline-none"
              onClick={() => setStep("create")}
              type="button"
            >
              <span className="grid size-11 place-items-center rounded-xl bg-emerald-100 text-emerald-900">
                <Building2 className="size-5" aria-hidden="true" />
              </span>
              <span className="mt-5 block text-xl font-black text-stone-950">
                Set up a food business
              </span>
              <span className="mt-2 block text-sm leading-6 text-stone-600">
                For owners or administrators creating a new organisation and its
                first location.
              </span>
            </button>
            <button
              className="rounded-2xl border border-stone-200 bg-surface p-6 text-left shadow-sm transition hover:border-emerald-400 hover:shadow-md focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:outline-none"
              onClick={() => setStep("join")}
              type="button"
            >
              <span className="grid size-11 place-items-center rounded-xl bg-amber-100 text-amber-900">
                <Users className="size-5" aria-hidden="true" />
              </span>
              <span className="mt-5 block text-xl font-black text-stone-950">
                Join my workplace
              </span>
              <span className="mt-2 block text-sm leading-6 text-stone-600">
                For staff or managers invited to an organisation that already
                uses SafePlate.
              </span>
            </button>
          </div>
        ) : (
          <Card className="mt-8 overflow-hidden shadow-lg">
            <CardHeader className="border-b border-stone-100 p-6 sm:p-7">
              <Button
                className="mb-2 -ml-2 self-start"
                onClick={() => setStep("choose")}
                size="sm"
                type="button"
                variant="ghost"
              >
                <ArrowLeft className="size-4" aria-hidden="true" />
                Back
              </Button>
              <span className="mb-3 grid size-10 place-items-center rounded-xl bg-amber-100 text-amber-900">
                <Mail className="size-5" aria-hidden="true" />
              </span>
              <CardTitle className="text-xl">Join your workplace</CardTitle>
              <CardDescription>
                Your manager creates the invitation from SafePlate’s Team page.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 sm:p-7">
              <form className="space-y-4" onSubmit={join}>
                <div className="space-y-2">
                  <Label htmlFor="workplace-invitation">Invitation link</Label>
                  <Input
                    id="workplace-invitation"
                    onChange={(event) => setInvitationInput(event.target.value)}
                    placeholder="Paste the link from your invitation email"
                    required
                    value={invitationInput}
                  />
                </div>
                {invitationError ? (
                  <p
                    className="text-sm font-semibold text-red-800"
                    role="alert"
                  >
                    {invitationError}
                  </p>
                ) : null}
                <Button className="w-full" type="submit">
                  Accept invitation
                </Button>
                <p className="text-xs leading-5 text-stone-500">
                  SafePlate does not use reusable organisation codes. Each
                  invitation is single-use, expires after seven days, and gives
                  you only the role and locations selected by your manager.
                </p>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </main>
  );
}
